import { setLocalStorage, getLocalStorage, removeLocalStorage } from "./localStorage";
import storageConstants from "../constants/storage";
const { AVS_TOKEN_EXPIRES_AT } = storageConstants;

let enabled = false; // flag to track if expiry handling is enabled
let expiresAtMs = null; // token expiry timestamp in milliseconds

// handler for proactive and reactive token refresh
let tokenRefreshHandler = null;

// handler to resolve if user is guest
let isGuestResolver = null;

// cooldown state for failed renewals
let lastRenewalFailureTime = null;
const RENEWAL_COOLDOWN_MS = 5000; // 5 seconds

// registers the token refresh handler function
function registerTokenRefreshHandler(fn) {
  tokenRefreshHandler = typeof fn === "function" ? fn : null;
}

// returns the registered token refresh handler
export function getTokenRefreshHandler() {
  return tokenRefreshHandler; // caller may pass a boolean (isPreflightMode) when invoking
}

// sets the expiry feature flag
export function setExpiryFeatureEnabled(flagEnabled) {
  enabled = !!flagEnabled;
}

// initializes access token expiry management state
export function initAuthExpiryState({ isGuestFn, tokenRefreshHandler }) {
  if (!enabled) return; // feature off
  isGuestResolver = isGuestFn;
  if (isGuestResolver && isGuestResolver()) {
    // defer token refresh handler registration until user is authenticated
    expiresAtMs = null;
    lastRenewalFailureTime = null; // reset cooldown on init
    return;
  }
  registerTokenRefreshHandler(tokenRefreshHandler);

  // populate in-memory expiresAt if already stored
  const stored = getLocalStorage(AVS_TOKEN_EXPIRES_AT);
  if (stored) {
    const parsed = parseExpiry(stored);
    if (parsed) expiresAtMs = parsed;
  }
}

// parses access token expiry from raw value (number/string), returns null if invalid.
export function parseExpiry(raw) {
  if (raw == null) return null;
  let expiresParsed = typeof raw === "number" ? raw : parseInt(raw, 10);
  if (!Number.isFinite(expiresParsed)) return null;
  if (expiresParsed < 1e12) expiresParsed *= 1000; // seconds to ms conversion if APIs return epoch seconds
  const now = Date.now();
  if (expiresParsed <= now) return null; // reject past values
  return expiresParsed;
}

// updates stored access token expiry timestamp from sessions endpoints
export function updateExpiryFromResultObj(resultObj) {
  if (!enabled || !resultObj) return false;
  const raw =
    resultObj?.profile?.session?.expire_at || // SESSIONS call response
    resultObj?.session?.expire_at; // DEVICESESSIONS call response
  const parsed = raw && parseExpiry(raw);
  if (!parsed) return false;
  expiresAtMs = parsed;
  setLocalStorage(AVS_TOKEN_EXPIRES_AT, String(parsed));
  return true;
}

// returns stored access token expiry timestamp in milliseconds
export function getExpiresAt() {
  return expiresAtMs;
}

// removes stored access token expiry timestamp
export function clearExpiry() {
  expiresAtMs = null;
  lastRenewalFailureTime = null; // reset cooldown
  removeLocalStorage(AVS_TOKEN_EXPIRES_AT);
}

// returns if expiry feature is enabled
export function isExpiryFeatureEnabled() {
  return enabled;
}

// returns if proactive token renewal is needed (with buffer in ms)
export function needsProactiveRenew(bufferMs = 5 * 60 * 1000) {
  if (!enabled) return false;
  if (isGuestUser()) return false;
  if (!expiresAtMs) return true;
  return Date.now() >= expiresAtMs - bufferMs;
}

// returns if current user is guest (unauthenticated)
export function isGuestUser() {
  return isGuestResolver && isGuestResolver();
}

// check if renewal is in cooldown period
export function isRenewalInCooldown() {
  if (!lastRenewalFailureTime) return false;
  return Date.now() - lastRenewalFailureTime < RENEWAL_COOLDOWN_MS;
}

// get remaining cooldown time in milliseconds
export function getRemainingCooldownMs() {
  if (!lastRenewalFailureTime) return 0;
  const elapsed = Date.now() - lastRenewalFailureTime;
  const remaining = RENEWAL_COOLDOWN_MS - elapsed;
  return remaining > 0 ? remaining : 0;
}

// mark renewal as failed (start cooldown)
export function markRenewalFailed() {
  lastRenewalFailureTime = Date.now();
}

// mark renewal as succeeded (clear cooldown)
export function markRenewalSucceeded() {
  lastRenewalFailureTime = null;
}

// get cooldown duration (for external use/config)
export function getRenewalCooldownMs() {
  return RENEWAL_COOLDOWN_MS;
}
